import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type {
  AuditLogResponse,
  EmployeeListResponse,
  HealthResponse,
  MeResponse,
  MeSubscription,
  PublicStatus,
  RevokeRequest,
  ServerCreate,
  ServerLoadView,
  ServerUpdate,
  ServerView,
  TenantListResponse,
  TenantRead,
  TenantUpdate,
  VpnProfileCreate,
  VpnProfileListResponse,
  VpnProfileRead,
  VpnProfileWithSubscription,
} from "./types";

// ─── Me / Portal ──────────────────────────────────────────────────────────

export function useMe(enabled = true) {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => (await api.get<MeResponse>("/v1/me")).data,
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useMeSubscription(profileId: string | null) {
  return useQuery({
    queryKey: ["me-subscription", profileId],
    queryFn: async () =>
      (await api.get<MeSubscription>(`/v1/me/vpn-profiles/${profileId}`)).data,
    enabled: profileId != null,
  });
}

export function useSelfRevokeProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/v1/me/vpn-profiles/${id}/revoke`, {});
    },
    onSuccess: () => {
      // Инвалидируем И список профилей (статус → revoked), И кэш подписки —
      // иначе карточка продолжит показывать sub URL/QR отозванного ключа.
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["me-subscription"] });
    },
  });
}

export function useSelfIssueProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const r = await api.post("/v1/me/vpn-profiles");
      return r.data as {
        id: string;
        sub_id: string;
        status: string;
        created_at: string;
        subscription_url: string;
        qr_base64: string;
      };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

// ─── Public ───────────────────────────────────────────────────────────────

export function usePublicStatus() {
  return useQuery({
    queryKey: ["public-status"],
    queryFn: async () => (await api.get<PublicStatus>("/public/status")).data,
    refetchInterval: 30_000,
  });
}

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => (await api.get<HealthResponse>("/health")).data,
    staleTime: 60_000,
    retry: false,
  });
}

// ─── Employees (shadow_users поиск) ───────────────────────────────────────

export function useEmployees(search: string) {
  return useQuery({
    queryKey: ["employees", search],
    queryFn: async () =>
      (
        await api.get<EmployeeListResponse>("/v1/admin/employees", {
          params: { search: search || undefined, limit: 50 },
        })
      ).data,
    staleTime: 15_000,
  });
}

// ─── VPN profiles (admin) ─────────────────────────────────────────────────

interface ListParams {
  page: number;
  page_size: number;
  status_filter?: string;
}

export function useVpnProfiles(params: ListParams) {
  return useQuery({
    queryKey: ["vpn-profiles", params],
    queryFn: async () =>
      (
        await api.get<VpnProfileListResponse>("/v1/admin/vpn-profiles", {
          params,
        })
      ).data,
  });
}

export function useVpnProfileDetail(id: string | null) {
  return useQuery({
    queryKey: ["vpn-profile", id],
    queryFn: async () =>
      (
        await api.get<VpnProfileWithSubscription>(
          `/v1/admin/vpn-profiles/${id}`,
        )
      ).data,
    enabled: id != null,
  });
}

export function useCreateVpnProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: VpnProfileCreate) =>
      (
        await api.post<VpnProfileWithSubscription>(
          "/v1/admin/vpn-profiles",
          body,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vpn-profiles"] }),
  });
}

export function useRevokeVpnProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; reason?: string }) =>
      (
        await api.post<VpnProfileRead>(
          `/v1/admin/vpn-profiles/${args.id}/revoke`,
          {
            reason: args.reason,
            notify_email: true,
          } as RevokeRequest,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["vpn-profiles"] }),
  });
}

// ─── Tenants (net:admin) ──────────────────────────────────────────────────

export function useTenants() {
  return useQuery({
    queryKey: ["tenants"],
    queryFn: async () =>
      (await api.get<TenantListResponse>("/v1/admin/tenants")).data,
  });
}

// Создание/удаление организаций — только в signaris-auth. Здесь лишь правка
// NET VPN-конфига (useUpdateTenant) + список (useTenants).
export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; body: TenantUpdate }) =>
      (await api.patch<TenantRead>(`/v1/admin/tenants/${args.id}`, args.body))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenants"] }),
  });
}

// ─── Servers ──────────────────────────────────────────────────────────────

export function useServers() {
  return useQuery({
    queryKey: ["servers"],
    queryFn: async () =>
      (await api.get<ServerView[]>("/v1/admin/servers")).data,
  });
}

export function useServerLoad() {
  return useQuery({
    queryKey: ["server-load"],
    queryFn: async () =>
      (await api.get<ServerLoadView[]>("/v1/admin/servers/load")).data,
    refetchInterval: 30_000,
  });
}

export function useCreateServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ServerCreate) =>
      (await api.post<ServerView>("/v1/admin/servers", body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["servers"] });
      qc.invalidateQueries({ queryKey: ["server-load"] });
    },
  });
}

export function useUpdateServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; body: ServerUpdate }) =>
      (await api.patch<ServerView>(`/v1/admin/servers/${args.id}`, args.body))
        .data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["servers"] });
      qc.invalidateQueries({ queryKey: ["server-load"] });
    },
  });
}

export function useDeleteServer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/admin/servers/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["servers"] });
      qc.invalidateQueries({ queryKey: ["server-load"] });
    },
  });
}

// ─── Audit log ────────────────────────────────────────────────────────────

export function useAuditLog(params: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ["audit", params],
    queryFn: async () =>
      (await api.get<AuditLogResponse>("/v1/admin/audit", { params })).data,
  });
}
